import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { HubConnectionState } from '@microsoft/signalr';
import { useAuthStore } from '@/auth/auth-store';
import { SCHEDULING_HUB_URL, createHubConnection } from '@/scheduling/realtime/signalr-connection';
import { firstAppointment } from '@/scheduling/realtime/use-scheduling-realtime';

/**
 * useEncounterAppointmentRealtime — the encounter's own appointment listener.
 *
 * WHY THIS EXISTS. Andrew, 2026-08-27: "pressing start will call create encounter which will update
 * the appointment to inprogress then will send signalr to update the UI." The first two steps
 * already worked — Start POSTs CreateEncounter and the backend moves the appointment — but nothing
 * in this app consumed the push, so the pill kept its pre-Start value until a manual reload.
 *
 * That gap is PROD-2028: `useSchedulingRealtime` is mounted ONLY by `scheduling/calendar-page.tsx`,
 * and even there its appointment handlers invalidate `['calendar-slots']` — never
 * `['appointment-details']`, which is what the encounter reads. So the encounter was deaf to
 * appointment changes from any source: its own Start, a sign, or a front-desk edit elsewhere.
 *
 * FLUTTER SHAPE, followed deliberately (`encounter_screen.dart:259-271`):
 *
 *     appointmentUpdateListener = await signalRCall(
 *       service: SignalRService.Scheduling,
 *       methodName: 'UpdateAppointments',
 *       onTrigger: (message) {
 *         Appointment appt = Appointment.fromJson(message.first);
 *         if (appt.id == widget.appointment?.id) bloc?.add(UpdateAppointment(appt));
 *       });
 *
 * Same hub, same method name, same id filter. Flutter replaces its single `state.appointment` from
 * the pushed payload; we invalidate instead and let the existing query refetch, because the
 * appointment read is already the authority on this screen (PROD-2030 inverted the precedence so the
 * fetched value beats the tap-time nav snapshot). Invalidating rather than trusting the payload also
 * means a partial or renamed push cannot corrupt the cache — the worst case is one wasted GET.
 *
 * WHY THE ENCOUNTER READ TOO. Start and sign move BOTH records server-side: the appointment status
 * and the encounter status (Draft → Completed on sign; see use-sign-lock.ts for the evidence). The
 * encounter status drives the primary action's label (Start / Continue / Edit), so refreshing one
 * without the other leaves the screen half-updated.
 *
 * ID GUARD IS LOAD-BEARING. The Scheduling hub is per-org: every appointment edit anywhere in the
 * organisation arrives here. Without the filter, one clinic's booking churn would invalidate every
 * open encounter's reads continuously.
 *
 * NOT a replacement for the calendar's subscription — that one does surgical cache merges for a
 * whole day's list. This is deliberately narrow: one appointment, two invalidations.
 */
export function useEncounterAppointmentRealtime(appointmentId: string | null | undefined): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!appointmentId) return;

    const getToken = () => useAuthStore.getState().getValidAccessToken();

    /* CONSTRUCTION CAN THROW, not just `start()`. SignalR resolves a relative hub URL through the
       DOM and throws `Cannot resolve '<url>'` outright when it decides the platform is not a
       browser (`HttpConnection._resolveUrl`, :562-566). A live encounter must not white-screen
       because its optional transport could not be built — the screen works on its fetch-on-mount
       reads either way, it just stops updating live. Same reasoning as the `startSafely` catch
       below; this is the synchronous half of that promise. */
    let schedulingHub: ReturnType<typeof createHubConnection>;
    try {
      schedulingHub = createHubConnection(SCHEDULING_HUB_URL, getToken);
    } catch {
      return;
    }

    schedulingHub.on('UpdateAppointments', (...args: unknown[]) => {
      // Shared with the calendar's listener so the two cannot disagree about the wire shape:
      // the server sometimes array-wraps the payload (Flutter reads `message.first`), sometimes not.
      const pushed = firstAppointment(args);

      // Per-org hub: only this appointment's changes concern this screen.
      if (!pushed?.id || pushed.id !== appointmentId) return;

      void queryClient.invalidateQueries({ queryKey: ['appointment-details', appointmentId] });
      void queryClient.invalidateQueries({ queryKey: ['encounter-by-appointment', appointmentId] });
    });

    // Same StrictMode-safe dance as `usePatientChartRealtime` — copied rather than reinvented. The
    // effect runs twice in dev, so a `start()` that resolves AFTER cleanup has run must stop itself
    // instead of leaving a live socket behind.
    let disposed = false;
    const startSafely = async (): Promise<void> => {
      try {
        if (schedulingHub.state === HubConnectionState.Disconnected) {
          await schedulingHub.start();
        }
        if (disposed && schedulingHub.state !== HubConnectionState.Disconnected) {
          await schedulingHub.stop();
        }
      } catch {
        // Auto-reconnect handles real failures; StrictMode negotiation aborts are dev-only noise.
        // A realtime failure must never break the screen: the encounter still works on its
        // fetch-on-mount reads, it just stops updating live.
      }
    };

    void startSafely();

    return () => {
      disposed = true;
      if (schedulingHub.state === HubConnectionState.Connected) {
        void schedulingHub.stop().catch(() => {});
      }
    };
  }, [appointmentId, queryClient]);
}
